"use client"

import { FormSection, FormShell, FormSkeleton, StickyActionBar } from "@/(admin)/components/form"
import AccessDenied from "@/components/custom-access-denied"
import { USERS } from "@/constants/admin-paths"
import { useCheckPermission } from "@/hook/use-check-permission"
import { useGuardedForm } from "@/hook/useGuardedForm"
import { flushAllRedisCache } from "@/services/redis-cache"
import { createPath } from "@/utils/create-path"
import { normalizeFormData } from "@/utils/normalize-form-data"
import { SaveOutlined } from "@ant-design/icons"
import { Button, Form, Input, Select } from "antd"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useState } from "react"
import { t } from "../../../../languages/i18n"
import { fetchRoleWebsiteOptions, RoleWebsiteOption } from "../../roles/services/roleWebsiteService"
import userService from "../services/userService"

const UpsertForm = ({ editMode = false }: { editMode?: boolean }) => {
  const searchParams = useSearchParams()
  const id = searchParams.get("id")
  const router = useRouter()
  const [form] = Form.useForm()
  const userServices = userService()
  const [roles, setRoles] = useState([])
  const [websiteOptions, setWebsiteOptions] = useState<RoleWebsiteOption[]>([])
  const { hasPermission: checkPermission } = useCheckPermission()

  const requiredPermission = editMode ? "update" : "create"
  const [isFormReady, setIsFormReady] = useState(false)
  const getNormalizedSnapshot = useCallback(() => {
    const values = form.getFieldsValue(true)
    const rawSnapshot = {
      username: values.username || "",
      email: values.email || "",
      firstName: values.firstName || "",
      lastName: values.lastName || "",
      password: values.password || "",
      role: values.role || null,
      websites: Array.isArray(values.websites) ? [...values.websites].sort() : [],
      language: values.language || "vi",
      emailSubscription: values.emailSubscription ?? true,
    }
    return normalizeFormData(rawSnapshot)
  }, [form])

  const { checkChange, commitSnapshot, message, handleCancel: guardedHandleCancel } = useGuardedForm({
    form,
    getSnapshot: getNormalizedSnapshot,
  })

  const getRoles = async () => {
    try {
      const response = await userServices.fetchAllRoles()
      const filteredRoles = response.roles.filter((role: any) => role.type !== "public")
      setRoles(filteredRoles || [])
    } catch (error) {
      message.error("Failed to fetch roles.")
    }
  }

  const getWebsites = async () => {
    try {
      const options = await fetchRoleWebsiteOptions()
      setWebsiteOptions(options)
    } catch (error) {
      message.error(t("Failed to load domain options."))
    }
  }

  const handleEmailBlur = async () => {
    const email = form.getFieldValue("email")
    if (email) {
      const emailExists = await userServices.checkEmailExists(email)
      if (emailExists.length > 0) {
        message.error(t("Email already exists."))
        form.setFields([
          {
            name: "email",
            errors: [t("Email already exists")],
          },
        ])
      }
    }
  }

  const handleUsernameBlur = async () => {
    const userName = form.getFieldValue("username")
    if (userName) {
      const usernameExists = await userServices.checkUsernameExists(userName)
      if (usernameExists.length > 0) {
        message.error(t("Account name already exists."))
        form.setFields([
          {
            name: "username",
            errors: [t("Account name already exists")],
          },
        ])
      }
    }
  }

  const handleFormSubmit = async (values: any) => {
    try {
      if (editMode) {
        if (!values.password) {
          delete values.password
        }
        await userServices.updateUser(Number(id), values)
      } else {
        await userServices.createUser(values)
      }
      try {
        await flushAllRedisCache()
      } catch {}
      commitSnapshot()
      form.resetFields()
      const targetPath = createPath(`${USERS.LIST.PATH}`, searchParams)
      router.push(targetPath)
    } catch (error) {
      message.error(t("An error occurred while submitting the form. Please try again."))
    }
  }

  const handleBack = () => {
    const goBackPath = createPath(`${USERS.LIST.PATH}`, searchParams)
    guardedHandleCancel(goBackPath)
  }

  const fetchUserData = async (userId: number) => {
    try {
      const userDataFetch = await userServices.getUserById(userId)
      if (userDataFetch) {
        form.setFieldsValue({
          ...userDataFetch,
          role: Number(userDataFetch.role.id),
          websites: Array.isArray(userDataFetch.websites)
            ? userDataFetch.websites.map((w: any) => Number(w.id))
            : [],
          language: userDataFetch.language || "en",
        })
        setIsFormReady(true)
      } else {
        message.warning(t("No user data found."))
      }
    } catch (error) {
      message.error(t("An error occurred while fetching user data."))
    }
  }

  useEffect(() => {
    if (editMode === false) {
      form.resetFields()
      setIsFormReady(true)
    }
  }, [editMode])

  useEffect(() => {
    if (isFormReady) {
      const animationId = requestAnimationFrame(() => {
        commitSnapshot()
      })
      return () => cancelAnimationFrame(animationId)
    }
  }, [isFormReady, commitSnapshot])

  useEffect(() => {
    if (id) {
      fetchUserData(Number(id))
    }
    getRoles()
    getWebsites()
  }, [id])

  if (!checkPermission("user", requiredPermission)) {
    return <AccessDenied />
  }

  if (editMode && !isFormReady) {
    return <FormSkeleton mainSections={2} sidebarCards={1} />
  }

  const crumbs = [
    { title: t("Người dùng"), href: createPath(`${USERS.LIST.PATH}`, searchParams) },
    { title: editMode ? t("Chỉnh sửa") : t("Tạo mới") },
  ]

  const main = (
    <FormSection title={t("Thông tin tài khoản")} collapsible={false}>
      <Form.Item
        label={t("Username")}
        name="username"
        rules={[
          { required: true, message: t("Account name cannot be left blank") },
          {
            pattern: /^[a-zA-Z0-9@._-]+$/,
            message: t("The account name cannot contain accents or special characters"),
          },
          { min: 6, message: t("Account name must be at least 6 characters") },
        ]}
      >
        <Input onBlur={handleUsernameBlur} autoComplete="current-username" />
      </Form.Item>

      <Form.Item
        label={t("Email")}
        name="email"
        rules={[
          { required: true, message: t("Email cannot be left blank") },
          { type: "email", message: t("Email is not in correct format") },
        ]}
      >
        <Input onBlur={handleEmailBlur} />
      </Form.Item>

      <Form.Item label={t("First Name")} name="firstName">
        <Input />
      </Form.Item>

      <Form.Item label={t("Last Name")} name="lastName">
        <Input />
      </Form.Item>

      <Form.Item
        label={t("Password")}
        name="password"
        rules={editMode ? [] : [{ required: true, message: t("Password cannot be left blank") }]}
        className="!mb-0"
      >
        <Input.Password autoComplete="new-password" visibilityToggle={false} />
      </Form.Item>
    </FormSection>
  )

  const sidebar = (
    <>
      <FormSection title={t("Vai trò")} collapsible={false} compact>
        <Form.Item name="role" className="!mb-0">
          <Select
            optionFilterProp="label"
            options={roles.map((role: any) => ({
              value: role.id,
              label: t(role.name),
            }))}
            placeholder={t("Chọn vai trò")}
          />
        </Form.Item>
      </FormSection>

      <FormSection title={t("Websites")} collapsible={false} compact>
        <Form.Item name="websites" className="!mb-0">
          <Select
            mode="multiple"
            allowClear
            optionFilterProp="label"
            options={websiteOptions.map(w => ({ value: w.id, label: w.label }))}
            placeholder={t("Chọn website")}
          />
        </Form.Item>
      </FormSection>
    </>
  )

  const actionBar = (
    <StickyActionBar
      right={
        <>
          <Button onClick={handleBack}>{t("Cancel")}</Button>
          <Button type="primary" icon={<SaveOutlined />} htmlType="submit">
            {editMode ? t("Edit") : t("Create")}
          </Button>
        </>
      }
    />
  )

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFormSubmit}
      initialValues={!editMode ? { role: "", websites: [], language: "vi", emailSubscription: true } : {}}
      onValuesChange={checkChange}
    >
      <FormShell
        title={editMode ? t("Chỉnh sửa người dùng") : t("Thêm người dùng mới")}
        crumbs={crumbs}
        main={main}
        sidebar={sidebar}
        actionBar={actionBar}
      />
    </Form>
  )
}

export default UpsertForm
